import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Translate, getTranslation } from "orion-components/i18n";
import { Button } from "@mui/material";
import { BaseWidget } from "../shared";
import { getWidgetState } from "orion-components/Profiles/Selectors";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { getSelectedContextData } from "orion-components/Profiles/Selectors";
import { userFeedsByIdSelector } from "orion-components/GlobalData/Feeds/selectors";
import { controlClicked } from "orion-components/SharedActions/deviceProfileActions";
import { feedService } from "client-app-core";
import { addContext, subscriptionReceived } from "orion-components/ContextualData/Actions";
import { unsubscribe } from "orion-components/ContextualData/contextStreaming";

const jsonata = require("jsonata");

const propTypes = {
	id: PropTypes.string,
	contextId: PropTypes.string,
	isForAlertProfile: PropTypes.bool,
	enabled: PropTypes.bool,
	deviceData: PropTypes.object,
	deviceStatus: PropTypes.string
};

const defaultProps = {
	deviceData: {},
	enabled: false,
	isForAlertProfile: false,
	contextId: "",
	id: "",
	deviceStatus: ""
};

const evaluateExpression = (template, data) => {
	if (template && data) {
		const expression = jsonata(template);
		return expression.evaluate(data);
	}
	return null;
};

/**
 * The function below first looks for the `statusTemplate` property in the entity's data. If it is not found there, it will check the feed data. If it is not found there either, it will look for the `status` property in the `device`. If the `status` property is also empty, it will simply return an empty string.
 *
 * @param {Object} device - The device object containing statusTemplate and entityData.
 * @param {Object} feedData - The feed data object containing statusTemplate information.
 * @returns {string} The evaluated statusTemplate or status property or an empty string.
 */
const getDeviceStatus = (device, feedData) => {
	let deviceStatus = "";

	if (device?.statusTemplate) {
		deviceStatus = evaluateExpression(device.statusTemplate, device.entityData);
	} else if (feedData?.statusTemplate) {
		deviceStatus = evaluateExpression(feedData.statusTemplate, device.entityData);
	} else if (device?.entityData?.properties?.status) {
		deviceStatus = device.entityData.properties.status;
	}

	return deviceStatus;
};

let prevFeedId = null;
let prevFeedData = null;
const AccessControlWidget = (props) => {
	const dispatch = useDispatch();

	const { id, contextId, isForAlertProfile, enabled, deviceData } = props;

	const widgetEnabled = useSelector((state) =>
		isForAlertProfile ? enabled : getWidgetState(state)(id, "enabled") || enabled
	);

	const dir = useSelector((state) => getDir(state));
	const readOnly = useSelector((state) => {
		return state?.appId === "replay-app" ? true : false;
	});

	const [isContextSet, SetisContextSet] = useState(false);

	const device = useSelector((state) =>
		isForAlertProfile && !isContextSet ? deviceData : getSelectedContextData(state)(contextId, "entity")
	);
	const { feedId = "" } = device;
	const feedData = useSelector((state) =>
		feedId !== prevFeedId ? userFeedsByIdSelector(feedId)(state) : prevFeedData
	);

	let deviceStatus = getDeviceStatus(device, feedData);

	useEffect(() => {
		if (isForAlertProfile) {
			subscribeDevice(contextId);
		} else {
			SetisContextSet(true);
		}
		return () => {
			dispatch(unsubscribe(contextId));
		};
	}, [isForAlertProfile]);

	const subscribeDevice = (entityId) => {
		const entityType = "device";
		feedService
			.streamEntityByType(entityId, entityType, (err, response) => {
				if (err) console.log(err);
				else {
					if (!response) return;
					prevFeedId = response.new_val?.feedId;
					prevFeedData = feedData;
					dispatch(addContext(entityId, response.new_val));
					SetisContextSet(true);
					deviceStatus = getDeviceStatus(response.new_val, feedData);
				}
			})
			.then((subscription) => {
				// Set feed subscription and add profile to subscriber array
				dispatch(subscriptionReceived(entityId, subscription.channel, "entity", null));
			});
	};

	const dispatchControlClicked = (feature) => {
		dispatch(controlClicked(feature, device.id));
	};

	const styles = {
		status: {
			color: "#FFFFFF",
			fontSize: 14,
			opacity: 0.5,
			margin: 8,
			lineHeight: "normal"
		},
		buttonContainer: {
			display: "flex",
			flexWrap: "wrap",
			gap: 8
		},
		controlButton: {
			minWidth: "fit-content",
			color: "#FFFFFF",
			padding: "4px 8px",
			fontSize: 12,
			width: "auto",
			maxWidth: "200px"
		}
	};

	return (
		<BaseWidget
			enabled={widgetEnabled}
			title={getTranslation("global.profiles.deviceProfile.accessControlWidget.title")}
			dir={dir}
			isForAlertProfile={isForAlertProfile}
		>
			<p style={styles.status}>
				<Translate
					value="global.profiles.deviceProfile.accessControlWidget.statusHeader"
					count={deviceStatus}
				/>
			</p>
			{!readOnly && (
				<div style={styles.buttonContainer}>
					{(device?.entityData?.properties?.commands || device?.entityData?.properties?.features)?.map(
						(feature) => (
							<Button
								key={`${feature.label?.replaceAll(" ", "-")}_control-button`}
								variant="contained"
								onClick={() => dispatchControlClicked(feature)}
								color="primary"
								style={styles.controlButton}
							>
								{feature.label}
							</Button>
						)
					)}
				</div>
			)}
		</BaseWidget>
	);
};

AccessControlWidget.propTypes = propTypes;
AccessControlWidget.defaultProps = defaultProps;
export default AccessControlWidget;
